/**
 * @file useInlineEditDirtyTracker.ts
 * @description Shared UI-layer dirtiness tracker for the contract-drawer
 *   edit-mode surface (SE-4347 AC-3 — Option A).
 *
 *   A contract drawer holds multiple inline-edit surfaces at once:
 *   a NEW-line-item row per category, and per-row inline edit drafts.
 *   None of those surfaces mutates `extractedData` until the user hits
 *   the row-local Save button, so server-snapshot-divergence in
 *   {@link useContractExtractionEditMode} stays `false` while the user
 *   still has unsaved keystrokes in flight. Without a second signal,
 *   pressing the drawer-level Cancel button silently discards typing.
 *
 *   This module exposes that second signal as a row-id-keyed set:
 *
 *   - {@link useInlineEditDirtyTracker} — owns the `Set<string>` and
 *     returns `markDirty(id)`, `markClean(id)`, `reset()`, and a
 *     derived `hasUnsavedInlineEdits` boolean. Called once at the
 *     drawer level so the aggregate survives the whole edit session.
 *   - {@link InlineEditDirtyProvider} / {@link useInlineEditDirtyContext} —
 *     carry the stable `markDirty`/`markClean` callbacks down to
 *     descendants (rows, per-category sections) without prop drilling.
 *     Descendants never see the Set itself, only the two callbacks.
 *
 *   Callers compose the two: the drawer mounts the tracker, plugs the
 *   callbacks into the Provider, and passes `hasUnsavedInlineEdits`
 *   into `useContractExtractionEditMode` so `isDirty` folds both the
 *   server-divergence and the UI-dirtiness signals. See the hook docs
 *   for row-id conventions.
 * @module features/contracts/hooks/useInlineEditDirtyTracker
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

/**
 * Return value of {@link useInlineEditDirtyTracker}.
 */
export interface UseInlineEditDirtyTrackerResult {
  /** Set of row ids that currently hold unsaved UI-layer edits. */
  readonly dirtyRowIds: ReadonlySet<string>;
  /** Derived convenience: `dirtyRowIds.size > 0`. */
  readonly hasUnsavedInlineEdits: boolean;
  /**
   * Register a row id as holding unsaved UI-layer edits. Idempotent —
   * calling with an already-dirty id is a no-op that preserves the
   * underlying Set reference so memoised consumers don't re-render.
   * @param rowId - Stable key identifying the inline-edit surface.
   *   Convention: `add:<categoryKey>` for the NEW-line-item row of a
   *   category; `edit:<categoryKey>:<index>` for an existing-row inline
   *   draft.
   */
  readonly markDirty: (rowId: string) => void;
  /**
   * Remove a row id from the dirty set. Called on row-local Save
   * success, row-local Discard, or when the draft text returns to its
   * empty/pristine baseline. No-op when the id is already clean.
   */
  readonly markClean: (rowId: string) => void;
  /**
   * Clear every dirty id in one shot. Called when the user confirms
   * the drawer-level discard (the refetch that follows will re-seed
   * rows from the server, so any per-row UI drafts are gone anyway)
   * and whenever the drawer closes. Preserves the Set reference when
   * already empty.
   */
  readonly reset: () => void;
}

/**
 * Hook that owns the shared `Set<string>` backing the inline-edit
 * dirtiness tracker. See the module docstring for the why; see
 * {@link UseInlineEditDirtyTrackerResult} for the API.
 * @returns The tracker API
 */
export function useInlineEditDirtyTracker(): UseInlineEditDirtyTrackerResult {
  const [dirtyRowIds, setDirtyRowIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const markDirty = useCallback((rowId: string) => {
    setDirtyRowIds(prev =>
      prev.has(rowId) ? prev : new Set([...prev, rowId])
    );
  }, []);

  const markClean = useCallback((rowId: string) => {
    setDirtyRowIds(prev =>
      prev.has(rowId) ? new Set([...prev].filter(id => id !== rowId)) : prev
    );
  }, []);

  const reset = useCallback(() => {
    setDirtyRowIds(prev => (prev.size === 0 ? prev : new Set()));
  }, []);

  const hasUnsavedInlineEdits = dirtyRowIds.size > 0;

  return useMemo(
    () => ({
      dirtyRowIds,
      hasUnsavedInlineEdits,
      markDirty,
      markClean,
      reset,
    }),
    [dirtyRowIds, hasUnsavedInlineEdits, markDirty, markClean, reset]
  );
}

/**
 * Context payload shape — just the two mutating callbacks. Consumers
 * never read `dirtyRowIds` through context because a row-level consumer
 * would otherwise re-render on every sibling row's dirty change, which
 * is useless churn for a keyboard-driven surface.
 */
interface InlineEditDirtyContextValue {
  readonly markDirty: (rowId: string) => void;
  readonly markClean: (rowId: string) => void;
}

const InlineEditDirtyContext =
  createContext<InlineEditDirtyContextValue | null>(null);

/**
 * Props for {@link InlineEditDirtyProvider}.
 */
export interface InlineEditDirtyProviderProps {
  /** Callback delivered to descendant consumers (wire to {@link UseInlineEditDirtyTrackerResult.markDirty}). */
  readonly markDirty: (rowId: string) => void;
  /** Callback delivered to descendant consumers (wire to {@link UseInlineEditDirtyTrackerResult.markClean}). */
  readonly markClean: (rowId: string) => void;
  /** Tree under which the tracker callbacks are visible. */
  readonly children: React.ReactNode;
}

/**
 * Provider component that carries `markDirty`/`markClean` to every
 * descendant row without explicit prop plumbing.
 * @param props - Provider props
 * @returns The wrapped subtree
 */
export function InlineEditDirtyProvider(props: InlineEditDirtyProviderProps) {
  const { markDirty, markClean, children } = props;
  const value = useMemo<InlineEditDirtyContextValue>(
    () => ({ markDirty, markClean }),
    [markDirty, markClean]
  );
  return (
    <InlineEditDirtyContext.Provider value={value}>
      {children}
    </InlineEditDirtyContext.Provider>
  );
}

InlineEditDirtyProvider.displayName = "InlineEditDirtyProvider";

/**
 * Safe no-op fallback used when {@link useInlineEditDirtyContext} is
 * consumed outside a provider (tests, read-only embeds like Player
 * Page Financials, archived drawers). Kept at module scope so every
 * out-of-provider consumer reads the same reference.
 */
const NO_OP_CONTEXT: InlineEditDirtyContextValue = {
  markDirty: () => {},
  markClean: () => {},
};

/**
 * Hook that reads the nearest {@link InlineEditDirtyProvider}. Returns
 * safe no-ops when there is no provider so callers outside the drawer
 * (e.g. read-only Player Page Financials) can call `markDirty` without
 * branching on the tree shape.
 * @returns The two tracker callbacks
 */
export function useInlineEditDirtyContext(): InlineEditDirtyContextValue {
  return useContext(InlineEditDirtyContext) ?? NO_OP_CONTEXT;
}
